import { fromSqft } from "@/lib/calculators/landCalculator";

// Length unit conversions to feet
export type LenUnit = "ft" | "m" | "cm" | "hath" | "in";

const TO_FT: Record<LenUnit, number> = {
  ft: 1,
  m: 3.28084,
  cm: 0.0328084,
  hath: 1.5,
  in: 1 / 12,
};

export function toFt(v: number, u: LenUnit): number {
  return v * TO_FT[u];
}

// ===== Shapes =====
export function rectangleSqft(length: number, width: number, unit: LenUnit): number {
  return toFt(length, unit) * toFt(width, unit);
}

export function triangleBaseHeightSqft(base: number, height: number, unit: LenUnit): number {
  return 0.5 * toFt(base, unit) * toFt(height, unit);
}

export function triangleHeronSqft(a: number, b: number, c: number, unit: LenUnit): number {
  const A = toFt(a, unit), B = toFt(b, unit), C = toFt(c, unit);
  const s = (A + B + C) / 2;
  const inside = s * (s - A) * (s - B) * (s - C);
  return inside > 0 ? Math.sqrt(inside) : NaN;
}

export function circleSqft(value: number, unit: LenUnit, kind: "radius" | "diameter") {
  const r = kind === "radius" ? toFt(value, unit) : toFt(value, unit) / 2;
  return {
    area: Math.PI * r * r,
    circumference: 2 * Math.PI * r,
    radiusFt: r,
  };
}

// ===== Multi-room =====
export interface Room {
  id: string;
  name: string;
  length: number;
  width: number;
  unit: LenUnit;
}

export function totalRoomsSqft(rooms: Room[]): { rows: { room: Room; sqft: number }[]; total: number } {
  const rows = rooms.map((r) => ({ room: r, sqft: rectangleSqft(r.length, r.width, r.unit) }));
  const total = rows.reduce((s, r) => s + (Number.isFinite(r.sqft) ? r.sqft : 0), 0);
  return { rows, total };
}

// ===== Practical: Flooring =====
export interface FlooringInputs {
  floorSqft: number;
  tileLength: number; // inches
  tileWidth: number;  // inches
  wastagePct: number; // %
  pricePerTile?: number; // ৳
}

export function computeFlooring(i: FlooringInputs) {
  const tileSqft = (i.tileLength * i.tileWidth) / 144;
  if (!tileSqft || !i.floorSqft) return { tileSqft: 0, baseTiles: 0, totalTiles: 0, totalCost: 0 };
  const baseTiles = i.floorSqft / tileSqft;
  const totalTiles = Math.ceil(baseTiles * (1 + i.wastagePct / 100));
  const totalCost = totalTiles * (i.pricePerTile ?? 0);
  return { tileSqft, baseTiles, totalTiles, totalCost };
}

// ===== Practical: Paint =====
export interface PaintInputs {
  wallSqft: number;
  coats: number;
  coveragePerLiter: number; // sq ft per L (1 coat)
  canSize: number;          // L per can
  pricePerLiter?: number;   // ৳/L
}

export function computePaint(i: PaintInputs) {
  if (!i.wallSqft || !i.coveragePerLiter) return { liters: 0, cans: 0, totalCost: 0 };
  const liters = (i.wallSqft * i.coats) / i.coveragePerLiter;
  const cans = Math.ceil(liters / i.canSize);
  const totalCost = liters * (i.pricePerLiter ?? 0);
  return { liters, cans, totalCost };
}

// ===== Practical: Carpet =====
export interface CarpetInputs {
  roomSqft: number;
  carpetWidthFt: number;
  pricePerFt?: number;
}

export function computeCarpet(i: CarpetInputs) {
  if (!i.roomSqft || !i.carpetWidthFt) return { lengthFt: 0, totalCost: 0 };
  const lengthFt = i.roomSqft / i.carpetWidthFt;
  return { lengthFt, totalCost: lengthFt * (i.pricePerFt ?? 0) };
}

// ===== Practical: Land cost =====
export function computeLandCost(sqft: number, pricePerSqft: number): number {
  return (sqft || 0) * (pricePerSqft || 0);
}

// Re-export helpers from land calculator for area-unit conversions
export { fromSqft };
